import { Component, effect, OnDestroy, signal } from '@angular/core';
import { SupplyDropService } from '../supply-drop.service';
import { ModalService } from '@app/modal/modal.service';
import { CaptchaModalComponent, CaptchaModalData } from '@app/modals/captcha-modal/captcha-modal.component';
import { NotificationService } from '@app/notification/notification.service';
import { Item } from '@models/item.interface';
import { SupplyDrop } from '@models/supply-drop.interface';
import { CurrencyComponent } from '@shared/currency/currency.component';
import { sleep } from '@utils';

const REEL_LENGTH = 30;
const WIN_INDEX = 24;
const REEL_DURATION_MS = 3000;
/**
 * Held after a reel lands so the result is readable: the reel animation and `REEL_DURATION_MS` end
 * on the same tick, and the sequence's `finally` tears the whole widget down via `resetToNone()`.
 */
const REEL_RESULT_DWELL_MS = 2000;

@Component({
  selector: 'app-supply-drop-widget',
  templateUrl: './supply-drop-widget.component.html',
  styleUrls: ['./supply-drop-widget.component.scss'],
  imports: [CurrencyComponent],
  host: {
    '[class.rolling]': 'supplyDropService.supplyDrop().status === "rolling"',
  },
})
export class SupplyDropWidgetComponent implements OnDestroy {
  readonly winIndex = WIN_INDEX;

  state = this.supplyDropService.supplyDrop;

  rollPhase = signal<'avatars' | 'skins' | null>(null);
  avatarReel = signal<string[]>([]);
  skinReel = signal<Item[]>([]);
  winningAvatar = signal<string>('');
  winningSkin = signal<Item | null>(null);
  reelTransform = signal('translateX(0%)');
  countdownLabel = signal('00:00');

  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(public supplyDropService: SupplyDropService, private modalService: ModalService, private notificationsService: NotificationService) {
    effect(() => {
      const status = this.state().status;

      if (status === 'running') {
        this.startCountdown(this.state().supplyDropInformation?.end_time ?? 0);
      } else {
        this.stopCountdown();
      }

      if (status === 'rolling' && this.supplyDropService.tryClaimRoll()) {
        void this.runRollSequence(this.state().supplyDrop);
      }
    });
  }

  ngOnDestroy() {
    this.stopCountdown();
  }

  openCaptchaModal() {
    const captcha = this.state().captcha;
    if (!captcha) {
      this.notificationsService.error('Captcha loading error, try refreshing', 'Supply Drop');
      return;
    }

    const data: CaptchaModalData = {
      image: captcha,
      title: 'Enter the characters to join the Supply Drop',
      submitLabel: 'Join Drop',
      onSubmit: (typed: string) => this.supplyDropService.joinSupplyDrop(typed),
    };

    this.modalService.open(CaptchaModalComponent, data);
  }

  /**
   * Runs the avatar reel then the skin reel and always ends in a reset to 'none' — even if the
   * winning-skin lookup misses or something throws mid-sequence. This is the mandated guard: tf2's
   * equivalent lookup can throw inside a render effect and jam the widget on 'rolling' forever;
   * here a miss is logged and the skin animation is skipped, and the `finally` below is the only
   * path back to 'none' so a thrown error can never leave the widget stuck.
   */
  private async runRollSequence(drop: SupplyDrop | null) {
    try {
      if (!drop) {
        return;
      }

      this.rollPhase.set('avatars');
      const winnerAvatar = drop.winner?.avatar ?? drop.winneravatar ?? '';
      this.winningAvatar.set(winnerAvatar);
      this.avatarReel.set(this.buildReel(drop.avatars ?? [], winnerAvatar));
      await this.playReel();
      await sleep(REEL_RESULT_DWELL_MS);

      this.rollPhase.set('skins');
      const winningSkinName = drop.result?.[0]?.skin;
      const winningSkin = drop.case?.skins?.find((skin) => skin.name === winningSkinName) ?? null;

      if (!winningSkin) {
        console.error('[SupplyDrop] winning skin not found in case skins — skipping skin reel animation', winningSkinName);
        this.winningSkin.set(null);
        this.skinReel.set([]);
      } else {
        this.winningSkin.set(winningSkin);
        this.skinReel.set(this.buildReel(drop.case?.skins ?? [], winningSkin));
        await this.playReel();
        await sleep(REEL_RESULT_DWELL_MS);
      }

      this.supplyDropService.postEndMessage(drop);
    } catch (err) {
      console.error('[SupplyDrop] roll sequence failed — resetting widget', err);
    } finally {
      this.rollPhase.set(null);
      this.supplyDropService.resetToNone();
      this.supplyDropService.releaseRoll();
    }
  }

  private buildReel<T>(pool: T[], winner: T): T[] {
    const reel: T[] = [];
    for (let i = 0; i < REEL_LENGTH; i++) {
      if (i === WIN_INDEX) {
        reel.push(winner);
        continue;
      }
      reel.push(pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : winner);
    }
    return reel;
  }

  private async playReel() {
    this.reelTransform.set('translateX(0%)');
    await sleep(50);

    const sizeCoefficient = 100 / REEL_LENGTH;
    const target = -(sizeCoefficient * WIN_INDEX) - sizeCoefficient / 2;
    this.reelTransform.set(`translateX(${target}%)`);
    await sleep(REEL_DURATION_MS);
  }

  private startCountdown(endTimeSeconds: number) {
    this.stopCountdown();
    this.updateCountdownLabel(endTimeSeconds);

    this.countdownIntervalId = setInterval(() => {
      this.updateCountdownLabel(endTimeSeconds);
    }, 1000);
  }

  private stopCountdown() {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  private updateCountdownLabel(endTimeSeconds: number) {
    const secondsLeft = Math.max(0, Math.floor(endTimeSeconds - Date.now() / 1000));
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    this.countdownLabel.set(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
  }
}
